/**
 * Ad landing page config per program. Each entry maps a URL slug to:
 *  - programMatcher: predicate to find the matching program in the DB list
 *  - headline / subheadline / heroImage: LP-specific hero copy
 *  - benefits: 4-6 short benefit lines shown in the hero area
 *  - metaTitle / metaDescription: <title> and <meta description> for the LP
 *  - accent: 'yellow' | 'emerald' — controls the primary CTA/accent colour
 *
 * The full feature list, price and tier logic all come from the program
 * document itself (fetched from /api/programs) so a coach edit in the DB
 * (or the idempotent programs_migration) is immediately reflected here.
 */
export const PROGRAM_LANDINGS = {
  'eletmodvalto': {
    programMatcher: (p) => p.level === 'Kezdő' && p.category !== 'consultation',
    headline: 'Végre olyan program, ami a munkád mellé van szabva',
    subheadline: 'Jobb tartás, kevésbé merev és fáradt hát, fenntartható fogyás — koplalás és extrém edzések nélkül, hetente 2-3 alkalommal Miskolcon a BB Gymben.',
    heroImage: 'https://images.unsplash.com/photo-1571902943202-507ec2618e8f?w=1600&h=900&fit=crop',
    benefits: [
      'Célzott gyakorlatok a merev, fáradt hát ellen',
      'Fenntartható fogyás — nincs koplalás, nincs csoda-diéta',
      'Egyénre szabott edzésterv és étrend a saját ritmusodhoz',
      'Beépített haladáskövető és heti check-in a fiókodban',
      'Kezdőknek, ülőmunkásoknak, műszakosoknak és időseknek is',
    ],
    metaTitle: 'Életmódváltó Program – Személyi edzés Miskolcon | Orosz Coaching',
    metaDescription: 'Jobb tartás, kevésbé merev és fáradt hát, fenntartható fogyás — a munkád mellé szabva. Kezdőknek is ideális. Foglalj ingyenes konzultációt!',
    accent: 'yellow',
  },
  'hipertrofia': {
    programMatcher: (p) => p.level === 'Haladó' && p.category !== 'consultation',
    headline: 'Építs mérhető izomtömeget az első 12 hétben',
    subheadline: 'Tudományosan megalapozott izomépítő program, ha már edzel, de elakadtál, vagy látványosabb eredményt szeretnél — a testalkatodhoz igazított tervezéssel és rád szabott táplálkozással.',
    heroImage: 'https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?w=1600&h=900&fit=crop',
    benefits: [
      'Tudományosan megalapozott izomépítő edzésterv (nem trendi módszerek)',
      'Tervezett, fokozatos erőfejlesztés — mérhető eredmény már az első 12 hétben',
      'A testalkatodhoz igazított gyakorlatválasztás',
      'Rád szabott táplálkozási terv, a fehérje, szénhidrát és zsír beállításával',
      'Étrendkiegészítő-tanácsadás, 24 órán belüli válaszadás',
    ],
    metaTitle: 'Izomépítés és formázás program – Miskolc | Orosz Coaching',
    metaDescription: 'Tudományosan megalapozott izomtömeg-növelés és erősödés Miskolcon. Ha már edzel, de elakadtál: mérhető eredmény már az első 12 hétben. Foglalj konzultációt!',
    accent: 'yellow',
  },
  'kiscsoportos-edzes': {
    programMatcher: (p) => p.variant === 'semi_private',
    headline: 'Személyi edzés minősége — barátoddal vagy párod­dal együtt',
    subheadline: 'Max. 3 fős zárt kiscsoport, mindenki saját, tudományosan megalapozott edzéstervvel. A legjobb ár-érték arányú személyi edzés Miskolcon — fejenként a legkedvezőbb egységáron.',
    heroImage: 'https://images.unsplash.com/photo-1571902943202-507ec2618e8f?w=1600&h=900&fit=crop',
    benefits: [
      'Max. 3 fős zárt kiscsoport — állandó társasággal, ítélkezésmentes közegben',
      'Egyénre szabott edzésterv MINDEN résztvevőnek (nem csoportos „egy méret mindenkinek")',
      'Fix heti időpont — rendszeresség és lendület',
      'Tudományosan megalapozott felépítés és folyamatos technikai irányítás',
      'Barát, pár vagy családtag együtt is jelentkezhet — együtt olcsóbb és motiválóbb',
      'Ideális kezdőknek és visszatérőknek (sérülés, szülés, kihagyás után)',
    ],
    metaTitle: 'Kiscsoportos Személyi Edzés – Duó & Trió, Miskolc | Orosz Coaching',
    metaDescription: 'Max. 3 fős zárt kiscsoportos személyi edzés Miskolcon. Egyéni terv barátoddal vagy párod­dal együtt — fejenként a legkedvezőbb egységáron. Foglalj konzultációt!',
    accent: 'emerald',
  },
  'premium-coaching': {
    programMatcher: (p) => p.level === 'Online' && p.category !== 'consultation',
    headline: '1:1 online mentorálás — bárhol vagy a világon',
    subheadline: 'Folyamatos személyes kísérés, garantált 24 órán belüli válaszadás munkanapokon, heti videós forma- és technikaelemzés. Teljesen rugalmas időbeosztás — a lehető leggyorsabb és legbiztonságosabb fejlődéshez.',
    heroImage: 'https://images.unsplash.com/photo-1518611012118-696072aa579a?w=1600&h=900&fit=crop',
    benefits: [
      'Rád szabott edzésterv — nem sablon',
      'Heti videós forma- és technikaelemzés minden gyakorlatról',
      'Üzenetváltás munkanapokon, garantált 24 órán belüli válaszadás',
      'Havi étrend-felülvizsgálat és finomhangolás',
      'Teljesen rugalmas időbeosztás — a saját életritmusodhoz igazítva',
      'Beépített haladáskövető és heti check-in a fiókodban',
    ],
    metaTitle: '1:1 Online Mentorálás – Bárhol a világon | Orosz Coaching',
    metaDescription: '1:1 online személyi mentorálás — heti videós elemzés, 24 órán belüli válaszadás, havi étrend-felülvizsgálat. Foglalj ingyenes konzultációt!',
    accent: 'yellow',
  },
};

export const LANDING_SLUGS = Object.keys(PROGRAM_LANDINGS);
